'use client';

import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { ArrowRight, Menu, X } from 'lucide-react';
import { useState } from 'react';

export function StructuredGridEditorialCorporateHomepage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="bg-white text-black font-sans">
      <header className="relative bg-white/90 backdrop-blur-sm border-b z-20">
        <div className="container mx-auto px-6 h-20 flex justify-between items-center">
          <h2 className="text-lg font-bold tracking-widest uppercase">
            HORIZON
          </h2>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
            <a href="#" className="text-gray-500 hover:text-black">
              Solutions
            </a>
            <a href="#" className="text-gray-500 hover:text-black">
              Perspectives
            </a>
            <a href="#" className="text-gray-500 hover:text-black">
              Firm
            </a>
            <Button>Contact Us</Button>
          </nav>
          <Button variant="ghost" className="md:hidden" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X/> : <Menu />}
          </Button>
        </div>
        {isMenuOpen && (
            <div className="md:hidden bg-white/95 border-t">
                <nav className="container mx-auto flex flex-col items-start gap-4 p-6">
                    <a href="#" className="text-gray-500 hover:text-black text-lg">Solutions</a>
                    <a href="#" className="text-gray-500 hover:text-black text-lg">Perspectives</a>
                    <a href="#" className="text-gray-500 hover:text-black text-lg">Firm</a>
                    <Button className="w-full mt-2">Contact Us</Button>
                </nav>
            </div>
        )}
      </header>
      <main className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-8 md:row-span-2 relative h-[60vh] md:h-[80vh]">
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent z-10" />
            <Image
              src="https://picsum.photos/seed/grid1/1200/1800"
              alt="A high-angle shot of a bustling, modern cityscape at dusk"
              fill
              className="object-cover"
              priority
              data-ai-hint="modern cityscape"
            />
            <div className="absolute bottom-0 left-0 p-8 z-10 text-white">
              <p className="text-sm uppercase tracking-widest">
                Global Macro
              </p>
              <h1 className="text-3xl md:text-5xl font-semibold mt-2">
                The New Economic Landscape
              </h1>
              <p className="mt-4 max-w-lg hidden md:block">
                Analyzing the key trends shaping markets in a post-pandemic
                world.
              </p>
            </div>
          </div>

          <div className="col-span-12 md:col-span-4 p-8 border">
            <p className="text-sm uppercase tracking-widest text-gray-500">
              Technology
            </p>
            <h2 className="text-2xl font-semibold mt-2">
              AI's Impact on Supply Chains
            </h2>
            <p className="mt-4 text-gray-600">
              How artificial intelligence is creating unprecedented efficiency
              and resilience.
            </p>
            <Button variant="link" className="p-0 mt-6 group">
              Read Analysis{' '}
              <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>

          <div className="col-span-12 md:col-span-4 relative h-80">
            <Image
              src="https://picsum.photos/seed/grid2/800/600"
              alt="Wind turbines on a hill at sunrise"
              fill
              className="object-cover"
              data-ai-hint="wind turbines"
            />
          </div>

          <div className="col-span-12 md:col-span-4 p-8 border flex flex-col justify-center">
            <p className="text-sm uppercase tracking-widest text-gray-500">
              Energy Transition
            </p>
            <h2 className="text-2xl font-semibold mt-2">
              Investing in a Greener Future
            </h2>
            <Button variant="link" className="p-0 mt-6 group">
              Explore Our Thesis{' '}
              <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>

          <div className="col-span-12 md:col-span-4 p-8 border">
            <p className="text-sm uppercase tracking-widest text-gray-500">
              About the Firm
            </p>
            <h2 className="text-2xl font-semibold mt-2">
              Data-Driven. Client-Focused.
            </h2>
            <p className="mt-4 text-gray-600">
              For over 30 years, Horizon has provided institutional investors
              with rigorous, independent research and strategic advice.
            </p>
            <Button variant="link" className="p-0 mt-6 group">
              Our Approach{' '}
              <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>

          <div className="col-span-12 md:col-span-4 relative h-80">
            <Image
              src="https://picsum.photos/seed/grid3/800/600"
              alt="Close up of stock market data on a screen"
              fill
              className="object-cover"
              data-ai-hint="stock market"
            />
          </div>

          <div className="col-span-12 md:col-span-4 p-8 bg-gray-900 text-white flex flex-col justify-center">
            <p className="text-sm uppercase tracking-widest text-gray-400">
              Careers
            </p>
            <h2 className="text-2xl font-semibold mt-2">
              Join a Team of Experts
            </h2>
            <p className="mt-4 text-gray-300">
              We are seeking talented individuals who are passionate about
              markets and driven by curiosity.
            </p>
            <Button variant="link" className="p-0 mt-6 text-white group">
              View Openings{' '}
              <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </div>
      </main>
      <footer className="bg-gray-100 border-t mt-12">
        <div className="container mx-auto px-6 py-16">
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8">
            <div className="sm:col-span-2 md:col-span-1">
              <h3 className="font-bold tracking-widest uppercase">HORIZON</h3>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>
                  <a href="#" className="hover:text-black">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-black">
                    Careers
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-black">
                    Media Inquiries
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Resources</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>
                  <a href="#" className="hover:text-black">
                    All Perspectives
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-black">
                    Client Login
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-black">
                    Disclosures
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Newsletter</h3>
              <p className="text-sm text-gray-600 mb-4">
                Sign up for weekly market insights from our analysts.
              </p>
              <div className="flex">
                <input
                  type="email"
                  placeholder="Email Address"
                  className="w-full border-gray-300 border p-2 text-sm"
                />
                <button className="bg-black text-white p-2 px-4 text-sm">
                  Sign Up
                </button>
              </div>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t text-xs text-gray-500 flex flex-col md:flex-row justify-between gap-4">
            <p>
              &copy; {new Date().getFullYear()} Horizon Global Advisors.
            </p>
            <p>New York / London / Singapore</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
